import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";

import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { DialogNotice } from "@/components/common/ActionDialog";
import { AsideRow, SettingsGrid, UnsavedBar } from "@/components/settings/settings-kit";
import { PaymentInfoForm, type PaymentMethodData } from "@/components/affiliate/PaymentInfoForm";
import { useAffiliate } from "@/contexts/affiliate/hooks";
import { paymentMethodsService, type PaymentMethod } from "@/services/paymentMethods.service";
import { countFilledMethods } from "@/utils/payoutMethods";

function normalise(data: PaymentMethodData[]) {
  return data
    .map((m) => ({
      id: m.paymentMethodId,
      fields: m.fieldValues
        .filter((f) => f.value && f.value.trim())
        .map((f) => `${f.fieldName}=${f.value.trim()}`)
        .sort()
        .join("&"),
    }))
    .filter((m) => m.fields)
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((m) => `${m.id}:${m.fields}`)
    .join("|");
}

function PaymentSkeleton() {
  return (
    <SettingsGrid
      form={
        <div className="rounded-lg border border-border bg-card">
          <div className="px-5 pb-3 pt-4 animate-pulse">
            <div className="h-4 w-32 rounded bg-muted" />
          </div>
          <div className="space-y-4 px-5 pb-5 animate-pulse">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="grid grid-cols-[7rem_1fr] items-center gap-3">
                <div className="h-3.5 w-16 rounded bg-muted" />
                <div className="h-9 w-full rounded-lg bg-muted" />
              </div>
            ))}
          </div>
        </div>
      }
      aside={
        <div className="rounded-lg border border-border bg-card">
          <div className="px-5 pb-3 pt-4 animate-pulse">
            <div className="h-4 w-36 rounded bg-muted" />
          </div>
          <div className="space-y-3 px-5 pb-5 animate-pulse">
            <div className="h-3.5 w-full rounded bg-muted" />
            <div className="h-3.5 w-2/3 rounded bg-muted" />
          </div>
        </div>
      }
    />
  );
}

/**
 * Where brands send commission. One method is enough; the aside lists the
 * ones on file so the affiliate can see what a brand can pay through.
 */
export function PaymentInfo({ supportedPaymentMethods }: { supportedPaymentMethods: string[] }) {
  const { details } = useAffiliate();
  const [methods, setMethods] = useState<PaymentMethod[] | null>(null);
  const [saved, setSaved] = useState<PaymentMethodData[]>([]);
  const [data, setData] = useState<PaymentMethodData[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (affiliateId: string) => {
    setLoading(true);
    try {
      const [all, info] = await Promise.all([
        paymentMethodsService.getAllPaymentMethods(),
        paymentMethodsService.getAffiliatePaymentInfo(affiliateId),
      ]);
      setMethods(all.filter((m) => m.isActive));
      const rows: PaymentMethodData[] = info.map((i) => ({
        paymentMethodId: i.paymentMethodId,
        fieldValues: Object.entries(i.fieldData).map(([fieldName, value]) => ({ fieldName, value })),
      }));
      setSaved(rows);
      setData(rows.map((r) => ({ ...r, fieldValues: r.fieldValues.map((f) => ({ ...f })) })));
    } catch {
      toast.error("Could not load your payment methods");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (details?.id) void load(details.id);
  }, [details?.id, load]);

  const hasChanges = useMemo(() => normalise(data) !== normalise(saved), [data, saved]);
  const filled = countFilledMethods(data);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges || !details?.id) return;
    if (filled < 1) {
      setError("Fill in at least one payment method");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const toUpdate = data
        .map((m) => ({
          paymentMethodId: m.paymentMethodId,
          fieldValues: m.fieldValues.filter((f) => f.value && f.value.trim()),
        }))
        .filter((m) => m.fieldValues.length > 0);
      if (toUpdate.length > 0) await paymentMethodsService.bulkUpdatePaymentMethod(toUpdate);
      await load(details.id);
      toast.success("Payment methods saved");
    } catch {
      toast.error("Could not save your payment methods");
    } finally {
      setSaving(false);
    }
  };

  if (loading && methods === null) return <PaymentSkeleton />;

  const onFile = (methods ?? [])
    .map((m) => {
      const row = data.find((d) => d.paymentMethodId === m.id);
      const value = row?.fieldValues.find((f) => f.value && f.value.trim())?.value?.trim();
      return value ? { name: m.name, value, supported: supportedPaymentMethods.includes(m.name) } : null;
    })
    .filter((x): x is { name: string; value: string; supported: boolean } => x !== null);

  return (
    <form onSubmit={save}>
      <SettingsGrid
        form={
          <DashboardCard id="SAFI-payment-info" title="Payment methods" bodyClassName="space-y-4 px-5 pb-5">
            {supportedPaymentMethods.length > 0 && (
              <DialogNotice tone="warning">
                This brand pays through {supportedPaymentMethods.join(", ")}. Fill in one of those to request a payout.
              </DialogNotice>
            )}
            <PaymentInfoForm
              methods={methods ?? []}
              paymentMethodsData={data}
              onChangePaymentMethodsData={(next) => {
                setData(next);
                setError("");
              }}
              showTitle={false}
              error={error}
              highlightedMethods={supportedPaymentMethods}
              minMethods={1}
            />
          </DashboardCard>
        }
        aside={
          <>
            <DashboardCard title="On file" bodyClassName="px-5 pb-3">
              {onFile.length === 0 ? (
                <p className="pb-2 text-sm text-muted-foreground">No payment method yet. Brands cannot pay you until one is on file.</p>
              ) : (
                onFile.map((m) => (
                  <AsideRow key={m.name} label={m.name}>
                    <span className="inline-flex max-w-full items-center gap-2">
                      <span className="truncate">{m.value}</span>
                      {m.supported && <span className="shrink-0 text-xs text-muted-foreground">used by this brand</span>}
                    </span>
                  </AsideRow>
                ))
              )}
              <p className="border-t border-border pt-3 text-xs text-muted-foreground">
                A brand pays through a method it supports, so a second one gives you more options.
              </p>
            </DashboardCard>
            <UnsavedBar visible={hasChanges} saving={saving} onCancel={() => setData(saved.map((r) => ({ ...r, fieldValues: r.fieldValues.map((f) => ({ ...f })) })))} saveLabel="Save methods" />
          </>
        }
      />
    </form>
  );
}
